import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import AppShell from 'components/AppShell';
import SelectResumeOption from './SelectResumeOption';
import SelectTemplate from './SelectTemplate';
import ResumeFormStepper from './ResumeFormStepper';
import HeadingsForm from './HeadingsForm';
import WorkExperienceForm from './WorkExperienceForm';
import EducationForm from './EducationForm';
import SkillsForm from './SkillsForm';
import useResumeStore from './store';

const ResumePreview = lazy(() => import('./ResumePreview'));

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: `calc(100vh - 145px)`,
    padding: theme.spacing(5),
    gap: theme.spacing(4),
    backgroundColor: theme.palette.shades['clr-white-900'],
    borderRadius: '4px',
  },
  formScreenContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    gap: theme.spacing(4),
  },
}));

const ResumeGenerator = () => {
  const classes = useStyles();

  const [activeFormStep, setActiveFormStep, setSelectedTemplate, selectedTemplate, resetStore] = useResumeStore(
    (state) => [state.activeFormStep, state.setActiveFormStep, state.setSelectedTemplate, state.selectedTemplate, state.reset]
  );

  const hasPersistedData = Boolean(selectedTemplate);
  const [currentScreen, setCurrentScreen] = useState(
    hasPersistedData ? 'FORM' : 'SELECT_TEMPLATE'
  );
  const [highestStep, setHighestStep] = useState(
    hasPersistedData ? Math.max(activeFormStep, 1) : 0
  );

  useEffect(() => {
    if (hasPersistedData && activeFormStep === 0) {
      setActiveFormStep(1);
      setHighestStep(1);
      setCurrentScreen('FORM');
    }

    return () => {
      resetStore();
    };
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasPersistedData) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPersistedData]);

  const handleGoBack = useCallback(() => {
    if (currentScreen === 'FORM' && activeFormStep > 1) {
      setActiveFormStep(activeFormStep - 1);
    } else if (currentScreen === 'FORM' && activeFormStep === 1) {
      setCurrentScreen('SELECT_TEMPLATE');
      setActiveFormStep(0);
    } else {
      navigate(-1);
    }
  }, [currentScreen, activeFormStep, navigate, setActiveFormStep]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (currentScreen === 'FORM' && activeFormStep > 0) {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
        handleGoBack();
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentScreen, activeFormStep, handleGoBack]);

  const handleOptionSelect = (id) => {
    if (id === 'CREATE_NEW') {
      setCurrentScreen('SELECT_TEMPLATE');
    } else {
      // TODO: handle import existing resume
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setCurrentScreen('FORM');
    setActiveFormStep(1);
    setHighestStep(1);
  };

  const handleFormNext = () => {
    if (activeFormStep < 5) {
      const nextStep = activeFormStep + 1;
      setActiveFormStep(nextStep);
      if (nextStep > highestStep) {
        setHighestStep(nextStep);
      }
    }
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= highestStep) {
      if (stepIndex === 0) {
        setCurrentScreen('SELECT_TEMPLATE');
        setActiveFormStep(0);
      } else {
        setCurrentScreen('FORM');
        setActiveFormStep(stepIndex);
      }
    }
  };

  return (
    <AppShell
      headerLabel='Create your Own Resume'
      showBackButton
      onGoBack={handleGoBack}
    >
      <Box className={classes.container}>
        {currentScreen === 'SELECT_OPTION' && (
          <SelectResumeOption onSelect={handleOptionSelect} />
        )}
        {currentScreen === 'SELECT_TEMPLATE' && (
          <Box className={classes.formScreenContainer}>
            <ResumeFormStepper
              activeStep={highestStep}
              currentStep={0}
              onStepClick={handleStepClick}
            />
            <SelectTemplate onSelect={handleTemplateSelect} />
          </Box>
        )}
        {currentScreen === 'FORM' && (
          <Box className={classes.formScreenContainer}>
            <ResumeFormStepper
              activeStep={highestStep}
              currentStep={activeFormStep}
              onStepClick={handleStepClick}
            />
            {activeFormStep === 1 && (
              <HeadingsForm
                onNext={handleFormNext}
              />
            )}
            {activeFormStep === 2 && (
              <WorkExperienceForm
                onNext={handleFormNext}
              />
            )}
            {activeFormStep === 3 && (
              <EducationForm
                onNext={handleFormNext}
              />
            )}
            {activeFormStep === 4 && (
              <SkillsForm
                onNext={handleFormNext}
              />
            )}
            {activeFormStep === 5 && (
              <Suspense fallback={<CircularProgress />}>
                <ResumePreview />
              </Suspense>
            )}
          </Box>
        )}
      </Box>

    </AppShell>
  );
};

export default ResumeGenerator;
